/**
 * Manual flight control — the geometry core.
 *
 * The operator drags a velocity vector out of the aircraft symbol on the map
 * (`docs/PLANS/manual-flight-control/README.md`). Everything that turns pixels
 * into a setpoint, and a setpoint back into a predicted position, lives here:
 * pure functions over plain numbers, no React, no MapLibre, no DOM. That is
 * deliberate — this module is where every unit-confusion bug in the feature
 * would otherwise hide, so it has to be testable on a bare node process.
 *
 * Three conventions hold throughout. Break one and the aircraft flies the wrong
 * way, so each is stated on every function that touches it:
 *
 * 1. **Screen pixels grow DOWN.** A drag "up" the screen is a NEGATIVE `dyPx`
 *    and means NORTH. Nothing else in this file re-derives that sign.
 * 2. **The wire frame is `map`** (`WIRE-CONTRACT.md`): `vx` is north, `vy` is
 *    east, both normalised to −1..1 against the envelope's `max_speed_ms`. The
 *    RC multiplies by the envelope it was granted, so a tampered client cannot
 *    request more speed than it was given.
 * 3. **Magnitude is clamped, never each axis.** `sqrt(vx² + vy²) ≤ 1`, so a
 *    diagonal is not √2 times faster than a cardinal drag.
 *
 * The calibration rule the whole design rests on: the outer ring is *where the
 * aircraft will be in 4 seconds at max speed* — 20 m at 5 m/s. The ring is
 * therefore the envelope limit, the position prediction and the clamp at once,
 * which is what lets the identical gesture work in a 900 px map and a 260 px
 * inset. Only the zoom changes, and `calibrateSurface` is what changes it.
 *
 * Distances are METRES, speeds METRES PER SECOND, angles DEGREES, times
 * MILLISECONDS unless a name says otherwise. Bearings are degrees true,
 * clockwise from north, 0–359. No axis ever carries a negative zero: `-0`
 * survives arithmetic, prints as "-0.0" in a readout, and reads as a direction.
 */
import { DEFAULT_ALTITUDE_PROFILE, type AltitudeProfile } from './altitude';
import { extractPolygonRing, pointInPolygonRing, type LatLng } from './mission-planning';

/**
 * Which input device is driving the surface. Not a screen-size guess: a touch
 * pointer needs a bigger dead zone, a bigger ring and a wider positional
 * uncertainty than a mouse on the same pixels.
 */
export type PointerMode = 'desktop' | 'touch';

/** Reference frame of a setpoint's `vx`/`vy`, per `WIRE-CONTRACT.md`. */
export type ControlFrame = 'map' | 'body';

/** Re-exported so a caller needs one import, and never declares a rival `{ lat, lng }`. */
export type { LatLng };

/**
 * The `clamped` vocabulary from `WIRE-CONTRACT.md`. Shared with the RC's
 * `control_state` frames on purpose — a limit is named once, in one word, in
 * every layer, so the operator never sees two phrasings of one refusal.
 */
export type ManualClampReason = 'speed' | 'vertical' | 'yaw' | 'ceiling' | 'floor' | 'geofence' | 'battery';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Every number this feature is built on, in one place, so the components import
 * them instead of re-typing them.
 *
 * The first block is transcribed from the "Timings and limits" table in
 * `docs/PLANS/manual-flight-control/WIRE-CONTRACT.md`, which is the single
 * source of truth across backend, RC app and COP. If one of these disagrees
 * with that table, the table is right — change it there first.
 *
 * The second block is COP-side surface geometry the contract does not specify.
 * Those are decided here, once, with the reasoning attached, rather than being
 * scattered as literals through the components.
 */
export const MANUAL_LIMITS = {
  // --- from WIRE-CONTRACT.md, do not change unilaterally --------------------
  /** Outer ring speed, m/s. First release; raise only once RTT is measured. */
  MAX_SPEED_MS: 5,
  /** Operator-selectable caps, m/s. A local multiplier only — the envelope is unchanged. */
  SPEED_PRESETS_MS: [1, 3, 5],
  MAX_VERTICAL_MS: 2,
  YAW_RATE_OPTIONS_DPS: [30, 60],
  MAX_YAW_RATE_DPS: 60,
  /** Ring calibration: the ring is this many seconds of travel at max speed. */
  RING_HORIZON_S: 4,
  RC_LOOP_HZ: 10,
  TTL_HOLD_MS: 400,
  TTL_NUDGE_MS: 1500,
  TTL_MAX_MS: 2000,
  HEARTBEAT_INTERVAL_MS: 1000,
  HEARTBEAT_TIMEOUT_MS: 10_000,
  GRANT_TIMEOUT_MS: 3000,
  /** Past this the aircraft has no position to drag from — the surface refuses. */
  TELEMETRY_STALE_MS: 3000,
  RTT_WARN_MS: 300,
  RTT_REFUSE_MS: 1000,
  LONG_PRESS_MS: 500,
  /** Movement beats time: this much drag cancels the long press. */
  LONG_PRESS_CANCEL_PX: 8,
  /** Below this the surface is display-only, per pointer mode. */
  MIN_SURFACE_PX: { desktop: 240, touch: 280 },
  MIN_RING_RADIUS_PX: { desktop: 64, touch: 80 },
  /** Inside the halo a drag is not a command — it is the operator finding the symbol. */
  GRAB_HALO_PX: { desktop: 44, touch: 56 },
  /**
   * Manual-flight altitude floor, m AGL. `AltitudeProfile` has no floor: its
   * `ALTITUDE_HARD_BOUNDS[0]` is a TAKEOFF minimum, which is a different
   * question. 5 m is the `alt_floor_m` from the contract's envelope example.
   */
  ALT_FLOOR_M: 5,

  // --- COP-side surface geometry, decided here ----------------------------
  /**
   * Usable drag length OUTSIDE the grab halo. The ring radius is halo +
   * travel, so the whole 0→1 range is travel the operator can actually feel;
   * 68 px over 5 m/s is ~0.07 m/s per pixel, finer than the airframe holds.
   */
  RING_TRAVEL_PX: { desktop: 68, touch: 72 },
  /** Room kept between the outer ring and the surface edge for the yaw ring, the altitude rail and edge labels. */
  RING_EDGE_GUTTER_PX: 40,
  /**
   * Centre detent of the altitude rail, px. Much smaller than the grab halo:
   * the halo is a 2-D "did you mean the symbol" radius, and a 44 px detent on a
   * 200 px rail would eat half the travel.
   */
  RAIL_DETENT_PX: { desktop: 12, touch: 16 },
  /**
   * How precisely a pointer lands, in CSS px — the input to `±N M AT THIS ZOOM`.
   * A mouse is limited by screen quantisation; a fingertip centroid lands within
   * ~4 mm, which is ~16 CSS px (1 CSS px = 1/96 in = 0.265 mm). The 44/56 px
   * grab halo covers the whole contact patch and is a different measurement.
   */
  POINTER_PRECISION_PX: { desktop: 2, touch: 16 },
  /**
   * Standoff kept between the 4-second projection and a geofence edge, m. Zero
   * would park the predicted position exactly on the line the RC enforces, and
   * GPS error alone is a metre or more.
   */
  GEOFENCE_BUFFER_M: 2,

  // --- surface furniture: the boxes drawn AROUND the ring ------------------
  //
  // The ring scales with the surface; these do not, which is why a 900 px map
  // and a 276 px minimap need `surfaceFurniture` to place them rather than one
  // set of literals. Every number here is the rendered size of a real element,
  // so a change to the markup has to come back here.
  /** STOP: `h-14 min-w-[9rem]` — the one control nothing may cover. */
  STOP_KEY_PX: { widthPx: 144, heightPx: 56 },
  /** Inset of the furniture that sits against a surface edge, CSS px (`*-4`). */
  SURFACE_EDGE_PAD_PX: 16,
  /** Clear space kept between two pieces of furniture, CSS px. */
  FURNITURE_GAP_PX: 8,
  /**
   * Readout width bounds, CSS px. The max fits the panel's own first row —
   * ~45 monospace characters at 11 px plus its padding — on a surface with room
   * for it; below that the row wraps rather than being clipped. The min is the
   * narrowest still worth reading, and what decides whether the panel can sit
   * beside STOP at all.
   */
  READOUT_WIDTH_PX: { min: 176, max: 360 },
  /**
   * Top strip the RECENTER block claims when it exists, CSS px: `top-3` inset +
   * a 44 px key + its caption + a gap. The readout stacks under it rather than
   * across it.
   */
  RECENTER_BLOCK_PX: 86,
  /** Altitude knob, CSS px (`size-11`) — the fixed part of the rail's height. */
  RAIL_KNOB_PX: 44,
  /** Usable travel from the rail's detent to either end, CSS px, on a surface with room for it. */
  RAIL_TRAVEL_PX: { desktop: 96, touch: 110 },
  /** Shortest rail still worth dragging, CSS px. */
  MIN_RAIL_TRAVEL_PX: 48,
  /** Where the touch rail's bottom edge sits, CSS px (`bottom-20`), clear of STOP. */
  RAIL_BOTTOM_PX: 80,
} as const;

/**
 * Refusal strings, verbatim from `WIRE-CONTRACT.md`. Exported so a reason is
 * never invented twice — the operator must read the same sentence whichever
 * layer produced it. `{n}`-style slots are the functions below.
 */
export const MANUAL_REFUSAL = {
  notCompanion: 'Manual flight is DJI companion only.',
  onGround: 'Aircraft is on the ground — use TAKEOFF first.',
  companionOffline: 'Companion app offline on the RC.',
  role: 'Requires operator role.',
  /**
   * The DEVICE is the problem — a phone, which no window size fixes. A surface
   * that is merely too small says how small it is instead
   * (`refusalSurfaceTooSmall`): telling an operator on a 27-inch monitor to
   * find a tablet, because they opened the details panel and narrowed the map
   * column, names the wrong thing to change.
   */
  viewportTooNarrow: 'Manual flight needs a tablet or larger.',
  noSecret: 'This aircraft has no command secret — manual flight refused.',
  pilotHasSticks: 'Pilot has the sticks — authority not available.',
  /**
   * The feature itself is not released yet.
   *
   * Unlike every other sentence here, this one is not about the aircraft, the
   * operator or the link: nothing the person reading it can do will clear it.
   *
   * "Not released" and not "not available": the key's own status word already
   * says UNAVAILABLE, which is the vocabulary of a TEMPORARY block and is what
   * every other refusal in this table is. The sentence has to carry the one
   * thing the status word cannot - that this is off by design and no amount of
   * waiting, launching or reconnecting will turn it on. It keeps the subject
   * ("Manual flight is ...") like its neighbours because it has to stand alone:
   * it is also the key's `title` and the hook's `unavailableReason`.
   *
   * The ONLY entry here with no server twin, and it must not grow one: the API
   * still grants sessions, so the RC app and any session already held keep
   * working. See `MANUAL_FLIGHT_RELEASED` in `drone-command-availability.ts`.
   */
  notReleased: 'Manual flight is not released yet.',
} as const;

/** `No telemetry for {n}s — cannot take control.` */
export function refusalStaleTelemetry(ageSeconds: number): string {
  return `No telemetry for ${Math.max(0, Math.round(ageSeconds))}s — cannot take control.`;
}

/** `Manual control held by {operator} since {HH:mm}Z.` — `sinceUtc` is preformatted `HH:mm`. */
export function refusalHeldBy(operator: string, sinceUtc: string): string {
  return `Manual control held by ${operator} since ${sinceUtc}Z.`;
}

/** `Link too slow for manual control ({n} ms).` */
export function refusalSlowLink(rttMs: number): string {
  return `Link too slow for manual control (${Math.round(rttMs)} ms).`;
}

/**
 * `Control surface is 210 px — manual flight needs 240.`
 *
 * The measurement, not a device verdict. This is the refusal an operator can
 * actually act on: it is produced when the box the surface was given is too
 * small, which on a desktop is usually the details panel narrowing the map
 * column and is fixed by closing it. Whether the DEVICE is capable at all is a
 * different question, answered before this one by `useManualDevice`.
 */
export function refusalSurfaceTooSmall(extentPx: number, requiredPx: number): string {
  return `Control surface is ${Math.round(Math.max(0, finiteOr(extentPx, 0)))} px — manual flight needs ${Math.round(requiredPx)}.`;
}

/**
 * Backend refusals the COP has to RECOGNISE, not merely render.
 *
 * Every other refusal is a sentence to display. These three change what the
 * client does next, so they are transcribed here verbatim from
 * `src/lib/manual-control.ts` — the same discipline as the table above, for the
 * stronger reason that a mismatch is a behaviour bug rather than a wording one.
 *
 * They all arrive as HTTP **409** with only `{ error: "<sentence>" }`; there is
 * no machine code on the wire to switch on. The status alone therefore cannot
 * decide anything: `noSession` is terminal (this console's authority is gone),
 * `rcSilent` is a transient the aircraft side is built to ride out, and
 * `nothingToRelease` is the release SUCCEEDING at what it was asked to do.
 * Treating all three as one status is how a hand-back that worked reads as a
 * failure and a two-second radio gap ends a live session.
 */
export const MANUAL_SERVER_REFUSAL = {
  /** Heartbeat refused because the RC has stopped reporting the session. */
  rcSilent: 'The RC is not reporting this session — manual authority not renewed.',
  /** `release` for an aircraft nobody holds — nothing to hand back. */
  nothingToRelease: 'No manual control session for this aircraft to release.',
  /** A frame for a session that no longer holds authority. Terminal. */
  noSession: 'No manual control session for this aircraft — request control first.',
} as const;

/** Whitespace- and case-insensitive, so a stray space cannot change behaviour. */
function normalizeRefusal(message: string | null | undefined): string {
  return (message ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Matched on the canonical sentence, with ONE distinctive phrase as a fallback.
 *
 * The phrase exists because the failure directions are not symmetric. Missing
 * this refusal ends a live manual session on the first two-second radio gap —
 * the thing the tolerance below is for — while a false positive only delays that
 * ending by three heartbeats. A backend that rewords its own sentence must not
 * silently cost the operator their aircraft, so the phrase carries the match
 * when the exact string drifts. It cannot collide with the other two: neither
 * mentions the RC reporting anything.
 */
export function isRcSilenceRefusal(message: string | null | undefined): boolean {
  const text = normalizeRefusal(message);
  if (text.length === 0) return false;
  return (
    text === normalizeRefusal(MANUAL_SERVER_REFUSAL.rcSilent) ||
    text.includes('not reporting this session')
  );
}

/**
 * `release` answered "nobody holds this aircraft".
 *
 * Deliberately NOT a prefix test: `noSession` opens with the same six words and
 * is the opposite answer. The distinguishing tail is what is matched.
 */
export function isNothingToReleaseRefusal(message: string | null | undefined): boolean {
  const text = normalizeRefusal(message);
  if (text.length === 0) return false;
  return (
    text === normalizeRefusal(MANUAL_SERVER_REFUSAL.nothingToRelease) ||
    text.includes('for this aircraft to release')
  );
}

/**
 * WGS84 equatorial circumference. Web Mercator scale is defined on the
 * EQUATORIAL radius, so this is deliberately not the mean radius used for
 * ground distances below — mixing the two puts a ~0.3% error in the zoom.
 */
const MERCATOR_CIRCUMFERENCE_M = 40_075_016.686;

/** MapLibre GL JS zoom is defined against 512 px tiles: the world is 512·2^z CSS px wide. */
const MAPLIBRE_TILE_PX = 512;

/** MapLibre's default zoom range; a calibration outside it cannot be applied. */
const MAPLIBRE_ZOOM_RANGE = [0, 22] as const;

/** Web Mercator latitude limit. Beyond it the projection has no finite scale. */
const MERCATOR_LAT_LIMIT = 85.051129;

/**
 * Mean-radius metres per degree of latitude. Matches the 6 371 km sphere
 * `haversineDistanceM` uses in `mission-planning.ts`, so a distance measured
 * one way agrees with an offset applied the other way.
 */
const METRES_PER_DEG_LAT = (Math.PI * 6_371_000) / 180;

// ---------------------------------------------------------------------------
// Small shared numerics
// ---------------------------------------------------------------------------

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const toDeg = (rad: number): number => (rad * 180) / Math.PI;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function finiteOr(value: number | undefined | null, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveOr(value: number | undefined | null, fallback: number): number {
  const v = finiteOr(value, fallback);
  return v > 0 ? v : fallback;
}

function wrap360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Quantise a normalised axis to 3 dp by TRUNCATING toward zero.
 *
 * Truncation, not rounding, because rounding can only ever grow a component:
 * two axes at 0.7071 round to 0.707… but a worst-case pair rounds up to a
 * magnitude above 1, which `WIRE-CONTRACT.md` forbids and the RC would clamp.
 * Truncating can never increase the magnitude. The cost is at most 1 mm/s of
 * commanded speed, well under what the airframe resolves — and quantising at
 * all is what keeps an unmoved pointer from emitting jitter frames.
 */
function truncUnit(value: number): number {
  const quantised = Math.trunc(value * 1000) / 1000;
  // `cos(90°)` is 6e-17, not 0, so a due-east drag truncates to -0 on the north
  // axis. Normalise it: -0 is not a direction, and it renders as "-0.0".
  return quantised === 0 ? 0 : quantised;
}

function withReason(
  existing: readonly ManualClampReason[],
  added: readonly ManualClampReason[],
): ManualClampReason[] {
  return [...new Set([...existing, ...added])];
}

// ---------------------------------------------------------------------------
// 1. Ring geometry — how big the control is, and whether it is a control at all
// ---------------------------------------------------------------------------

export interface SurfaceSize {
  widthPx: number;
  heightPx: number;
}

export interface RingOptions {
  pointer?: PointerMode;
  /** Envelope max speed, m/s. Defaults to the contract's 5. */
  maxSpeedMs?: number;
  /** Preset rings to draw inside the outer ring, m/s. */
  speedPresetsMs?: readonly number[];
}

export interface RingGeometry {
  pointer: PointerMode;
  /** Shorter side of the surface, px — the dimension the ring has to fit inside. */
  extentPx: number;
  /** Radius of the outer ring = `maxSpeedMs`, in CSS px. */
  outerRadiusPx: number;
  /** Radius inside which a drag is a hover, not a command, in CSS px. */
  deadZonePx: number;
  /** Envelope speed the outer ring stands for, m/s. */
  maxSpeedMs: number;
  /** Ground radius the outer ring stands for, m: `RING_HORIZON_S` × `maxSpeedMs`. */
  ringRadiusM: number;
  /** Where to draw each speed preset, ascending. */
  presetRings: readonly { speedMs: number; radiusPx: number }[];
  /** False → the surface is display-only. Render `refusal`, not a control. */
  controllable: boolean;
  /**
   * Why the surface cannot be flown from, or null when it can — naming the
   * measurement that failed, never the device. A caller that knows the DEVICE
   * is the problem (a phone) says so itself, before it ever gets here.
   */
  refusal: string | null;
  /** The shorter side this surface had to reach to be a control, CSS px. */
  minExtentPx: number;
}

/**
 * Ring radii in CSS px for a given surface, plus the verdict on whether the
 * surface is large enough to fly from at all.
 *
 * The radius is `grab halo + usable travel`, capped so the ring stays inside
 * the surface with a gutter for the yaw ring and altitude rail, and floored at
 * the contract's minimum radius. Note the radius does NOT grow with a large
 * surface: a bigger ring means a finer zoom (see `calibrateSurface`), so a ring
 * that scaled with the map would show the operator LESS ground on a bigger
 * screen. A constant gesture size is the point.
 *
 * Geometry is returned even when `controllable` is false, so a display-only
 * surface can still draw the ring greyed out; the caller renders `refusal`.
 */
export function ringGeometry(surface: SurfaceSize, options: RingOptions = {}): RingGeometry {
  const pointer = options.pointer ?? 'desktop';
  const maxSpeedMs = positiveOr(options.maxSpeedMs, MANUAL_LIMITS.MAX_SPEED_MS);

  const widthPx = Math.max(0, finiteOr(surface.widthPx, 0));
  const heightPx = Math.max(0, finiteOr(surface.heightPx, 0));
  const extentPx = Math.min(widthPx, heightPx);

  const deadZonePx = MANUAL_LIMITS.GRAB_HALO_PX[pointer];
  const minRadiusPx = MANUAL_LIMITS.MIN_RING_RADIUS_PX[pointer];
  const preferredPx = deadZonePx + MANUAL_LIMITS.RING_TRAVEL_PX[pointer];
  const availablePx = extentPx / 2 - MANUAL_LIMITS.RING_EDGE_GUTTER_PX;
  const outerRadiusPx = Math.max(minRadiusPx, Math.min(preferredPx, availablePx));

  const minExtentPx = MANUAL_LIMITS.MIN_SURFACE_PX[pointer];
  const controllable = extentPx >= minExtentPx;
  const presets = options.speedPresetsMs ?? MANUAL_LIMITS.SPEED_PRESETS_MS;

  return {
    pointer,
    extentPx,
    outerRadiusPx,
    deadZonePx,
    maxSpeedMs,
    ringRadiusM: MANUAL_LIMITS.RING_HORIZON_S * maxSpeedMs,
    presetRings: presets
      .filter((speedMs) => Number.isFinite(speedMs) && speedMs > 0 && speedMs <= maxSpeedMs)
      .sort((a, b) => a - b)
      .map((speedMs) => ({ speedMs, radiusPx: (outerRadiusPx * speedMs) / maxSpeedMs })),
    controllable,
    refusal: controllable ? null : refusalSurfaceTooSmall(extentPx, minExtentPx),
    minExtentPx,
  };
}

// ---------------------------------------------------------------------------
// 1b. Surface furniture — where the boxes AROUND the ring go
// ---------------------------------------------------------------------------

export interface SurfaceFurnitureOptions {
  pointer?: PointerMode;
  /** True while RECENTER is on screen. It owns the top centre, so the readout stacks under it. */
  recenterShown?: boolean;
}

/** Absolute placement of a panel, CSS px from the surface's own edges. */
export interface FurnitureBox {
  leftPx: number;
  /** From the TOP edge, or null when the box hangs off the bottom. */
  topPx: number | null;
  /** From the BOTTOM edge, or null when it hangs off the top. */
  bottomPx: number | null;
  maxWidthPx: number;
}

export interface SurfaceFurniture {
  readout: FurnitureBox;
  /** Usable travel from the altitude rail's detent to either end, CSS px. */
  railTravelPx: number;
  /** Total height the rail occupies at that travel, CSS px. */
  railHeightPx: number;
  /** True when the readout had to leave the bottom edge because STOP is there. */
  readoutMoved: boolean;
}

/**
 * Where the readout and the altitude rail fit, for a surface of this size.
 *
 * The ring scales with the surface and the furniture does not, so at minimap
 * scale the fixed boxes collide with each other and with the edges. Two of
 * those collisions are not cosmetic:
 *
 * - The readout is drawn AFTER the STOP key and has an opaque background, so at
 *   276 px it paints over the emergency stop. STOP stays where it is — it is
 *   the one control an operator must find without looking — and the readout
 *   moves to the top instead, which is where the touch layout already puts it
 *   for the same reason (the bottom corners belong to the control and to STOP).
 * - The touch rail is anchored `bottom-20` and is `travel*2 + knob` tall, which
 *   at 316 px starts 28 px ABOVE the surface and clips its own UP/CEIL label and
 *   full-climb position. The travel shortens to fit rather than the rail hanging
 *   off the top of a control the operator is flying with.
 *
 * Pure geometry over the rendered sizes in `MANUAL_LIMITS`, so the arithmetic is
 * checkable without a browser — which matters because the failure it prevents
 * is invisible in a unit test that only asks whether an element rendered.
 */
export function surfaceFurniture(
  surface: SurfaceSize,
  options: SurfaceFurnitureOptions = {},
): SurfaceFurniture {
  const pointer = options.pointer ?? 'desktop';
  const widthPx = Math.max(0, finiteOr(surface.widthPx, 0));
  const heightPx = Math.max(0, finiteOr(surface.heightPx, 0));
  const edgePx = MANUAL_LIMITS.SURFACE_EDGE_PAD_PX;
  const gapPx = MANUAL_LIMITS.FURNITURE_GAP_PX;
  const { min: minWidthPx, max: maxWidthPx } = MANUAL_LIMITS.READOUT_WIDTH_PX;

  // Room left of STOP. On a finger STOP is in a bottom CORNER and the readout is
  // at the top regardless, so the bottom is never offered there.
  const besideStopPx =
    pointer === 'touch'
      ? 0
      : widthPx / 2 - MANUAL_LIMITS.STOP_KEY_PX.widthPx / 2 - edgePx - gapPx;
  const readoutMoved = besideStopPx < minWidthPx;

  const topPx = options.recenterShown ? MANUAL_LIMITS.RECENTER_BLOCK_PX : edgePx;
  const readout: FurnitureBox = readoutMoved
    ? {
        leftPx: edgePx,
        topPx,
        bottomPx: null,
        // Bounded by the surface, so a long first row wraps inside the box
        // instead of being clipped by the surface's `overflow-hidden` parent.
        maxWidthPx: Math.max(0, Math.min(maxWidthPx, widthPx - edgePx * 2)),
      }
    : {
        leftPx: edgePx,
        topPx: null,
        bottomPx: edgePx,
        maxWidthPx: Math.max(0, Math.min(maxWidthPx, besideStopPx)),
      };

  // The rail is centred on a mouse and anchored above STOP on a finger, so the
  // height it has to fit inside differs.
  const availablePx =
    pointer === 'touch'
      ? heightPx - MANUAL_LIMITS.RAIL_BOTTOM_PX - gapPx
      : heightPx - edgePx * 2;
  const railTravelPx = Math.max(
    MANUAL_LIMITS.MIN_RAIL_TRAVEL_PX,
    Math.min(
      MANUAL_LIMITS.RAIL_TRAVEL_PX[pointer],
      Math.floor((availablePx - MANUAL_LIMITS.RAIL_KNOB_PX) / 2),
    ),
  );

  return {
    readout,
    railTravelPx,
    railHeightPx: railTravelPx * 2 + MANUAL_LIMITS.RAIL_KNOB_PX,
    readoutMoved,
  };
}

// ---------------------------------------------------------------------------
// 2. Zoom calibration — make the ring mean 20 m of ground
// ---------------------------------------------------------------------------

/**
 * Ground metres covered by one CSS pixel at a MapLibre zoom and latitude.
 *
 * MapLibre GL JS zoom is defined against 512 px tiles, so the world is
 * 512·2^zoom CSS px wide — one zoom level coarser than the 256 px raster
 * convention. Getting that factor wrong is a 2× scale error.
 */
export function metresPerPixelAtZoom(zoom: number, latitudeDeg: number): number {
  const lat = clamp(finiteOr(latitudeDeg, 0), -MERCATOR_LAT_LIMIT, MERCATOR_LAT_LIMIT);
  const worldPx = MAPLIBRE_TILE_PX * 2 ** finiteOr(zoom, 0);
  return (MERCATOR_CIRCUMFERENCE_M * Math.cos(toRad(lat))) / worldPx;
}

function rawZoomFor(metresPerPixel: number, latitudeDeg: number): number {
  const lat = clamp(finiteOr(latitudeDeg, 0), -MERCATOR_LAT_LIMIT, MERCATOR_LAT_LIMIT);
  const mpp = positiveOr(metresPerPixel, 1);
  return Math.log2((MERCATOR_CIRCUMFERENCE_M * Math.cos(toRad(lat))) / (MAPLIBRE_TILE_PX * mpp));
}

/**
 * The MapLibre zoom at which one CSS pixel covers `metresPerPixel` of ground at
 * this latitude — the exact inverse of `metresPerPixelAtZoom`.
 *
 * Clamped to MapLibre's zoom range and rounded to 4 dp (≈0.007% of scale) so
 * that recomputing a calibration from unchanged inputs yields a byte-identical
 * zoom and the caller does not animate the map on floating-point noise.
 */
export function zoomForMetresPerPixel(metresPerPixel: number, latitudeDeg: number): number {
  const [min, max] = MAPLIBRE_ZOOM_RANGE;
  return Math.round(clamp(rawZoomFor(metresPerPixel, latitudeDeg), min, max) * 1e4) / 1e4;
}

export interface SurfaceCalibration {
  /** Ground radius the outer ring stands for, m — 4 s at max speed. */
  ringRadiusM: number;
  /** Ground metres per CSS px at `zoom`. Always consistent with `zoom`, even when clamped. */
  metresPerPixel: number;
  /** The zoom the surface should hold for the ring to mean `ringRadiusM`. */
  zoom: number;
  /**
   * True when MapLibre's zoom range could not deliver the calibration — the
   * ring then means more (or less) ground than `ringRadiusM`, and the caller
   * must not present it as the 4-second horizon.
   */
  zoomClamped: boolean;
}

/**
 * The zoom that makes the ring mean what it claims: `RING_HORIZON_S` seconds of
 * travel at max speed, 20 m at the contract's 5 m/s.
 *
 * This is the rule the whole control surface rests on. Every layout — full map,
 * inset, minimap — uses the same gesture and the same ring, and differs only in
 * the zoom this returns.
 *
 * `metresPerPixel` is derived back from the (possibly clamped) zoom rather than
 * from the ideal, so the two returned values can never disagree; a caller that
 * trusted an ideal `metresPerPixel` against a clamped zoom would draw the
 * prediction in the wrong place.
 */
export function calibrateSurface(ring: RingGeometry, latitudeDeg: number): SurfaceCalibration {
  const idealMpp = ring.ringRadiusM / Math.max(1, ring.outerRadiusPx);
  const raw = rawZoomFor(idealMpp, latitudeDeg);
  const zoom = zoomForMetresPerPixel(idealMpp, latitudeDeg);
  const [min, max] = MAPLIBRE_ZOOM_RANGE;
  return {
    ringRadiusM: ring.ringRadiusM,
    metresPerPixel: metresPerPixelAtZoom(zoom, latitudeDeg),
    zoom,
    zoomClamped: raw < min || raw > max,
  };
}

// ---------------------------------------------------------------------------
// 3 & 4. Pointer → setpoint, with a dead zone and a magnitude clamp
// ---------------------------------------------------------------------------

/** Pointer offset from the aircraft symbol, CSS px. `dyPx` grows DOWN the screen. */
export interface PointerOffsetPx {
  dxPx: number;
  dyPx: number;
}

export interface SetpointOptions {
  /** Aircraft heading, degrees true. Only used for `frame: 'body'`. */
  headingDeg?: number;
  /** Wire frame. `map` (north/east) is the COP default per the contract. */
  frame?: ControlFrame;
  /** Compass bearing of screen-up, degrees. 0 = north-up; pass `map.getBearing()` for a rotated map. */
  mapBearingDeg?: number;
  /** Operator speed preset, m/s. Caps the magnitude locally; never raises the envelope. */
  speedCapMs?: number;
}

export interface ManualSetpoint {
  /** North (frame `map`) or forward (frame `body`), −1..1, normalised to `maxSpeedMs`. */
  vx: number;
  /** East (frame `map`) or right (frame `body`), −1..1. */
  vy: number;
  /** `sqrt(vx² + vy²)`, 0..1. Never above 1. */
  magnitude: number;
  /** Ground speed the setpoint implies, m/s — for the readout, not the wire. */
  speedMs: number;
  /** Compass bearing of the commanded motion, degrees true, or null while hovering. */
  bearingDeg: number | null;
  /** True when this is an explicit stop: all axes zero. */
  isHover: boolean;
  frame: ControlFrame;
  /** Limits that shortened this vector, in the contract's vocabulary. */
  clamped: ManualClampReason[];
  /** The envelope speed `vx`/`vy` are normalised against, m/s. */
  maxSpeedMs: number;
}

/** An explicit zero — the `STOP` key, a released pointer, and a `pointercancel`. */
export function hoverSetpoint(
  frame: ControlFrame = 'map',
  maxSpeedMs: number = MANUAL_LIMITS.MAX_SPEED_MS,
): ManualSetpoint {
  return {
    vx: 0,
    vy: 0,
    magnitude: 0,
    speedMs: 0,
    bearingDeg: null,
    isHover: true,
    frame,
    clamped: [],
    maxSpeedMs: positiveOr(maxSpeedMs, MANUAL_LIMITS.MAX_SPEED_MS),
  };
}

function buildSetpoint(
  magnitude: number,
  bearingDeg: number,
  frame: ControlFrame,
  maxSpeedMs: number,
  headingDeg: number,
  clamped: readonly ManualClampReason[],
): ManualSetpoint {
  const north = magnitude * Math.cos(toRad(bearingDeg));
  const east = magnitude * Math.sin(toRad(bearingDeg));

  let vx = north;
  let vy = east;
  if (frame === 'body') {
    // Rotate the ground vector into nose-relative axes: forward is the heading,
    // right is 90° clockwise of it.
    const h = toRad(wrap360(headingDeg));
    vx = north * Math.cos(h) + east * Math.sin(h);
    vy = -north * Math.sin(h) + east * Math.cos(h);
  }
  vx = truncUnit(vx);
  vy = truncUnit(vy);

  // Recompute from the quantised axes so the readout can never claim a speed
  // the wire does not carry.
  const mag = Math.min(1, Math.hypot(vx, vy));
  return {
    vx,
    vy,
    magnitude: mag,
    speedMs: mag * maxSpeedMs,
    bearingDeg: mag === 0 ? null : wrap360(bearingDeg),
    isHover: mag === 0,
    frame,
    clamped: [...clamped],
    maxSpeedMs,
  };
}

/** Scale an existing setpoint to a new magnitude, keeping its direction and frame. */
function withMagnitude(
  setpoint: ManualSetpoint,
  magnitude: number,
  added: readonly ManualClampReason[],
): ManualSetpoint {
  if (setpoint.magnitude <= 0) return setpoint;
  const factor = Math.max(0, magnitude) / setpoint.magnitude;
  const vx = truncUnit(setpoint.vx * factor);
  const vy = truncUnit(setpoint.vy * factor);
  const mag = Math.min(1, Math.hypot(vx, vy));
  return {
    ...setpoint,
    vx,
    vy,
    magnitude: mag,
    speedMs: mag * setpoint.maxSpeedMs,
    bearingDeg: mag === 0 ? null : setpoint.bearingDeg,
    isHover: mag === 0,
    clamped: withReason(setpoint.clamped, added),
  };
}

/**
 * Pointer offset (CSS px, from the aircraft symbol, `dyPx` growing DOWN) →
 * a normalised velocity setpoint.
 *
 * - Inside the grab halo the result is a HOVER, not a small velocity: the
 *   operator is finding the symbol, not commanding 0.4 m/s.
 * - The 0→1 range is measured from the EDGE of the halo, not from the centre.
 *   Measuring from the centre would step the aircraft straight to ~40% of max
 *   the instant the pointer left the halo — a lurch nobody asked for.
 * - Past the outer ring the MAGNITUDE clamps to 1 and `clamped` reports
 *   `'speed'`; the direction is preserved, so a diagonal overdrag is 5 m/s
 *   diagonally, not 7 m/s.
 * - A speed preset below the envelope max clamps the same way, since it is a
 *   local multiplier and the envelope the RC holds is unchanged.
 */
export function setpointFromPointer(
  offset: PointerOffsetPx,
  ring: RingGeometry,
  options: SetpointOptions = {},
): ManualSetpoint {
  const frame = options.frame ?? 'map';
  const dx = finiteOr(offset.dxPx, 0);
  const dy = finiteOr(offset.dyPx, 0);
  const distancePx = Math.hypot(dx, dy);

  if (distancePx <= ring.deadZonePx) return hoverSetpoint(frame, ring.maxSpeedMs);

  const travelPx = Math.max(1, ring.outerRadiusPx - ring.deadZonePx);
  const requested = (distancePx - ring.deadZonePx) / travelPx;

  const speedCapMs = clamp(positiveOr(options.speedCapMs, ring.maxSpeedMs), 0, ring.maxSpeedMs);
  const capMagnitude = ring.maxSpeedMs > 0 ? speedCapMs / ring.maxSpeedMs : 0;
  const magnitude = Math.min(requested, capMagnitude);

  // Screen-up (negative dy) is bearing 0 in a north-up map; `mapBearingDeg`
  // carries the rotation when the operator has turned the map.
  const bearingDeg = wrap360(finiteOr(options.mapBearingDeg, 0) + toDeg(Math.atan2(dx, -dy)));

  return buildSetpoint(
    magnitude,
    bearingDeg,
    frame,
    ring.maxSpeedMs,
    finiteOr(options.headingDeg, 0),
    requested > capMagnitude ? ['speed'] : [],
  );
}

// ---------------------------------------------------------------------------
// 5. Projection and the geofence clamp
// ---------------------------------------------------------------------------

/** Metres per degree of longitude at a latitude — collapses toward the poles. */
function metresPerDegLng(latitudeDeg: number): number {
  const lat = clamp(finiteOr(latitudeDeg, 0), -MERCATOR_LAT_LIMIT, MERCATOR_LAT_LIMIT);
  return METRES_PER_DEG_LAT * Math.cos(toRad(lat));
}

/**
 * Offset a position by a local north/east displacement in metres.
 *
 * Flat-earth on purpose: everything here works at the 20 m ring scale, where a
 * tangent-plane approximation is accurate to well under a centimetre and a
 * great-circle solution would only add a way to get the sign wrong.
 */
function offsetPosition(position: LatLng, northM: number, eastM: number): LatLng {
  return {
    lat: position.lat + northM / METRES_PER_DEG_LAT,
    lng: position.lng + eastM / metresPerDegLng(position.lat),
  };
}

export interface ProjectedPosition extends LatLng {
  /** Ground distance from the aircraft to the projection, m. */
  distanceM: number;
  /** Bearing flown to get there, degrees true, or null when hovering. */
  bearingDeg: number | null;
}

/**
 * Where the aircraft ends up if it holds this setpoint for `horizonS` seconds.
 *
 * At full deflection this lands exactly on the outer ring — that identity is
 * what makes the ring simultaneously the limit and the prediction.
 */
export function projectSetpoint(
  position: LatLng,
  setpoint: ManualSetpoint,
  horizonS: number = MANUAL_LIMITS.RING_HORIZON_S,
): ProjectedPosition {
  const distanceM = setpoint.speedMs * Math.max(0, finiteOr(horizonS, 0));
  if (setpoint.bearingDeg === null || distanceM === 0) {
    return { lat: position.lat, lng: position.lng, distanceM: 0, bearingDeg: null };
  }
  const bearing = toRad(setpoint.bearingDeg);
  const moved = offsetPosition(position, distanceM * Math.cos(bearing), distanceM * Math.sin(bearing));
  return { ...moved, distanceM, bearingDeg: setpoint.bearingDeg };
}

export interface GeofenceClamp {
  /** The setpoint to send — shortened only when the projection would leave the boundary. */
  setpoint: ManualSetpoint;
  clamped: boolean;
  /** Metres from the aircraft to the boundary along the commanded bearing; null when there is no bearing or no hit. */
  distanceToEdgeM: number | null;
  /** True when the aircraft is ALREADY outside the boundary. */
  outside: boolean;
}

/** Nearest positive hit of a ray from the origin against a closed ring, in local metres. */
function rayToRingDistance(
  ring: readonly { east: number; north: number }[],
  dirEast: number,
  dirNorth: number,
): number | null {
  let nearest: number | null = null;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j];
    const b = ring[i];
    const sEast = b.east - a.east;
    const sNorth = b.north - a.north;
    // 2-D cross products: denominator zero means the edge is parallel to the ray.
    const denom = dirEast * sNorth - dirNorth * sEast;
    if (Math.abs(denom) < 1e-12) continue;
    const t = (a.east * sNorth - a.north * sEast) / denom;
    const u = (a.east * dirNorth - a.north * dirEast) / denom;
    if (t >= 0 && u >= 0 && u <= 1 && (nearest === null || t < nearest)) nearest = t;
  }
  return nearest;
}

/**
 * Shorten a setpoint so its `horizonS`-second projection stays inside a keep-in
 * boundary, and report how far the edge is along the commanded bearing.
 *
 * The boundary is a GeoJSON Polygon (or its outer ring, `[lng, lat]` pairs, the
 * order `mission-planning.ts` uses). Inside/outside reuses that module's
 * `pointInPolygonRing`, so the COP's answer matches the backend GO/NO-GO gate
 * rather than being a second implementation with its own edge cases.
 *
 * When the aircraft is ALREADY outside, the vector is passed through untouched
 * and `outside` is set. Clamping there would strand the aircraft: the only way
 * back inside is a velocity vector, and this is a hint to the operator, not the
 * safety gate — the hard envelope is enforced on the RC (`README.md`).
 */
export function clampToGeofence(
  setpoint: ManualSetpoint,
  position: LatLng,
  boundary: GeoJSON.Geometry | number[][] | null | undefined,
  options: { horizonS?: number; bufferM?: number } = {},
): GeofenceClamp {
  const ring = Array.isArray(boundary) ? boundary : extractPolygonRing(boundary);
  const unchanged: GeofenceClamp = { setpoint, clamped: false, distanceToEdgeM: null, outside: false };
  if (!ring || ring.length < 3) return unchanged;

  if (!pointInPolygonRing(position.lat, position.lng, ring)) {
    return { ...unchanged, outside: true };
  }
  if (setpoint.bearingDeg === null) return unchanged;

  // Local tangent plane centred on the aircraft: the whole problem is now
  // planar metres, where a ray/segment intersection is exact.
  const perDegLng = metresPerDegLng(position.lat);
  const local = ring.map((point) => ({
    east: (point[0] - position.lng) * perDegLng,
    north: (point[1] - position.lat) * METRES_PER_DEG_LAT,
  }));

  const bearing = toRad(setpoint.bearingDeg);
  const distanceToEdgeM = rayToRingDistance(local, Math.sin(bearing), Math.cos(bearing));
  if (distanceToEdgeM === null) return unchanged;

  const horizonS = Math.max(0, finiteOr(options.horizonS, MANUAL_LIMITS.RING_HORIZON_S));
  const bufferM = Math.max(0, finiteOr(options.bufferM, MANUAL_LIMITS.GEOFENCE_BUFFER_M));
  const projectionM = setpoint.speedMs * horizonS;
  const allowedM = Math.max(0, distanceToEdgeM - bufferM);

  if (projectionM <= allowedM || projectionM === 0) {
    return { setpoint, clamped: false, distanceToEdgeM, outside: false };
  }
  return {
    setpoint: withMagnitude(setpoint, (setpoint.magnitude * allowedM) / projectionM, ['geofence']),
    clamped: true,
    distanceToEdgeM,
    outside: false,
  };
}

// ---------------------------------------------------------------------------
// 6. Dead-reckoned ghost
// ---------------------------------------------------------------------------

export interface TelemetryFix extends LatLng {
  /** Age of the fix at render time, ms. */
  ageMs: number;
}

export interface GhostPosition extends LatLng {
  /** Age of the fix this was extrapolated from, ms. */
  ageMs: number;
  /** How much of that age was actually extrapolated, ms (capped). */
  extrapolatedMs: number;
  /** Ground distance the ghost sits ahead of the last fix, m. */
  advancedM: number;
  /**
   * True past `TELEMETRY_STALE_MS`.
   *
   * NOT a refusal, and it never was one in the code. An in-session refusal is
   * deliberately not this client's job — the RC's dead-man ends a session, not a
   * label on a screen (`useManualControl`) — while
   * `manualControlAvailability` DOES refuse to start one on a fix this old.
   *
   * What this obliges the surface to do is SAY SO, because everything drawn
   * freezes together and still looks live: the readout carries `FIX +Ns` in
   * amber, the live region says it once when it crosses, and FLY TO HERE — the
   * one gesture that turns this frozen position into a dispatched command — is
   * refused until a fresh fix arrives. The dimmed ghost is the cue, not the
   * message.
   */
  stale: boolean;
}

/**
 * The aircraft's predicted position NOW, from the last fix plus its velocity —
 * the client-side prediction that covers the uplink/downlink latency gap, so
 * the operator drags from where the aircraft is rather than from where it was.
 *
 * `courseDeg` is course over ground, which is what the telemetry heading stands
 * in for; in a crosswind the nose and the track differ, and the track is what
 * moves the symbol.
 *
 * Extrapolation is capped at `TELEMETRY_STALE_MS` because past that point the
 * surface stops vouching for the position rather than continuing to guess at it
 * (see `stale`). Without the cap a 30-second-old fix would draw a confidently
 * wrong symbol 150 m from the aircraft — worse than a stale one, because it
 * looks live.
 */
export function deadReckonPosition(
  fix: TelemetryFix,
  motion: { groundSpeedMs: number; courseDeg: number },
  options: { maxExtrapolationMs?: number } = {},
): GhostPosition {
  const ageMs = Math.max(0, finiteOr(fix.ageMs, 0));
  const capMs = Math.max(0, finiteOr(options.maxExtrapolationMs, MANUAL_LIMITS.TELEMETRY_STALE_MS));
  const extrapolatedMs = Math.min(ageMs, capMs);
  const speedMs = Math.max(0, finiteOr(motion.groundSpeedMs, 0));
  const advancedM = (speedMs * extrapolatedMs) / 1000;
  const stale = ageMs > MANUAL_LIMITS.TELEMETRY_STALE_MS;

  if (advancedM === 0) {
    return { lat: fix.lat, lng: fix.lng, ageMs, extrapolatedMs, advancedM: 0, stale };
  }
  const course = toRad(wrap360(finiteOr(motion.courseDeg, 0)));
  const moved = offsetPosition(fix, advancedM * Math.cos(course), advancedM * Math.sin(course));
  return { ...moved, ageMs, extrapolatedMs, advancedM, stale };
}

// ---------------------------------------------------------------------------
// 7. Altitude rail
// ---------------------------------------------------------------------------

/**
 * The limit set the RC enforces, in the contract's wire shape (snake_case
 * deliberately — this is the `envelope` object of `control_request`, not a
 * client-side view model).
 */
export interface ManualEnvelope {
  max_speed_ms: number;
  max_vertical_ms: number;
  max_yaw_rate_dps: number;
  alt_floor_m: number;
  alt_ceiling_m: number;
  setpoint_ttl_max_ms: number;
  heartbeat_timeout_ms: number;
}

/**
 * The envelope derived from an org's altitude profile plus the contract's hard
 * caps. The authoritative copy is built server-side and never from client
 * input; this is the same derivation so the COP can show the operator the
 * limits it is about to be granted.
 *
 * The ceiling is the profile's regulatory ceiling. The floor comes from
 * `MANUAL_LIMITS.ALT_FLOOR_M`, because `AltitudeProfile` has no floor of its
 * own — its lower bound is a takeoff minimum, a different question.
 */
export function manualEnvelope(
  profile: AltitudeProfile = DEFAULT_ALTITUDE_PROFILE,
  overrides: Partial<ManualEnvelope> = {},
): ManualEnvelope {
  return {
    max_speed_ms: MANUAL_LIMITS.MAX_SPEED_MS,
    max_vertical_ms: MANUAL_LIMITS.MAX_VERTICAL_MS,
    max_yaw_rate_dps: MANUAL_LIMITS.MAX_YAW_RATE_DPS,
    alt_floor_m: MANUAL_LIMITS.ALT_FLOOR_M,
    alt_ceiling_m: profile.ceiling_m,
    setpoint_ttl_max_ms: MANUAL_LIMITS.TTL_MAX_MS,
    heartbeat_timeout_ms: MANUAL_LIMITS.HEARTBEAT_TIMEOUT_MS,
    ...overrides,
  };
}

export interface AltitudeRailInput {
  /** Rail offset from the centre detent, CSS px. NEGATIVE is up, as screen y grows down. */
  offsetPx: number;
  /** Usable travel from the detent to either end of the rail, CSS px. */
  travelPx: number;
  /** Current altitude AGL, m. */
  altitudeM: number;
  envelope: ManualEnvelope;
  pointer?: PointerMode;
}

export interface AltitudeCommand {
  /** −1..1, UP positive, normalised to `max_vertical_ms` per the contract. */
  vz: number;
  /** Vertical speed the setpoint implies, m/s, up positive — for the readout. */
  verticalMs: number;
  /** True when the rail is in its detent: no vertical command. */
  isHover: boolean;
  /** Which limit shortened the command, in the contract's vocabulary. */
  clamped: 'ceiling' | 'floor' | null;
  /** Metres remaining to the limit in the commanded direction; null while in the detent. */
  marginM: number | null;
}

/**
 * Rail offset (CSS px, negative up) → a normalised `vz`, clamped by the
 * envelope's floor and ceiling.
 *
 * Clamped on the same 4-second horizon as the lateral vector, not at the limit
 * itself: a climb is shortened as the ceiling approaches so the projected
 * altitude stops at it, which is a taper the operator can feel rather than a
 * wall the aircraft hits. `clamped` names which limit did it, matching the RC's
 * `control_state.clamped` words.
 *
 * Descending is always available above the ceiling, and climbing below the
 * floor — a command that reduces the violation is never blocked.
 */
export function altitudeCommandFromRail(input: AltitudeRailInput): AltitudeCommand {
  const pointer = input.pointer ?? 'desktop';
  const detentPx = MANUAL_LIMITS.RAIL_DETENT_PX[pointer];
  const offsetPx = finiteOr(input.offsetPx, 0);
  const travelPx = Math.max(1, finiteOr(input.travelPx, 1) - detentPx);
  const maxVerticalMs = positiveOr(input.envelope.max_vertical_ms, MANUAL_LIMITS.MAX_VERTICAL_MS);

  const still: AltitudeCommand = { vz: 0, verticalMs: 0, isHover: true, clamped: null, marginM: null };
  if (Math.abs(offsetPx) <= detentPx) return still;

  // Negative offset is up the screen, and up is positive vz on the wire.
  const direction = offsetPx < 0 ? 1 : -1;
  const requested = Math.min(1, (Math.abs(offsetPx) - detentPx) / travelPx);

  const altitudeM = finiteOr(input.altitudeM, 0);
  const limitM = direction > 0 ? input.envelope.alt_ceiling_m : input.envelope.alt_floor_m;
  const marginM = Math.max(0, direction > 0 ? limitM - altitudeM : altitudeM - limitM);

  const horizonM = requested * maxVerticalMs * MANUAL_LIMITS.RING_HORIZON_S;
  const clamped = horizonM > marginM ? (direction > 0 ? 'ceiling' : 'floor') : null;
  const allowed = clamped === null ? requested : Math.min(requested, marginM / (maxVerticalMs * MANUAL_LIMITS.RING_HORIZON_S));

  const vz = truncUnit(direction * allowed);
  return {
    vz,
    verticalMs: vz * maxVerticalMs,
    isHover: vz === 0,
    clamped,
    marginM,
  };
}

// ---------------------------------------------------------------------------
// 8. `FLY TO HERE` confirmation gate
// ---------------------------------------------------------------------------

export interface FlyToHereGate {
  /** Ground width the surface currently shows, m. */
  visibleWidthM: number;
  /** Positional uncertainty of the tap at this scale, m — computed, never assumed. */
  uncertaintyM: number;
  /** The operator-facing form: `±3 M AT THIS ZOOM`. */
  uncertaintyLabel: string;
}

/**
 * How imprecise a `FLY TO HERE` tap is at this scale.
 *
 * It no longer decides WHETHER to confirm. That used to be gated on the visible
 * map width (< 150 m), and the arithmetic made the gate backwards: metres per
 * pixel come from the ring (`ringRadiusM / outerRadiusPx`, ~0.15 on a desktop
 * ring), so 150 m of visible ground is a surface under ~990 px — meaning the
 * DEFAULT full-map layout on any normal monitor confirmed nothing, and a 500 ms
 * press dispatched a real GO-TO with no arming and no confirmation. Every
 * FLY TO HERE goes through the confirm bar now, at every scale; this function
 * answers the question that bar exists to put in front of the operator.
 *
 * The uncertainty is `metres per pixel × pointer precision`, so it tracks the
 * real scale of the real surface instead of being a literal that goes stale the
 * first time a layout changes. It floors at 1 m: "±0 m" is a claim no GNSS fix
 * supports.
 */
export function flyToHereGate(
  surfaceWidthPx: number,
  metresPerPixel: number,
  pointer: PointerMode = 'desktop',
): FlyToHereGate {
  const widthPx = Math.max(0, finiteOr(surfaceWidthPx, 0));
  const mpp = Math.max(0, finiteOr(metresPerPixel, 0));
  const uncertaintyM = mpp * MANUAL_LIMITS.POINTER_PRECISION_PX[pointer];
  return {
    visibleWidthM: widthPx * mpp,
    uncertaintyM,
    uncertaintyLabel: `±${Math.max(1, Math.round(uncertaintyM))} M AT THIS ZOOM`,
  };
}

export interface ManualTarget extends LatLng {
  /** Ground distance from the aircraft to the target, m. */
  distanceM: number;
  /** Bearing from the aircraft to the target, degrees true; null when they coincide. */
  bearingDeg: number | null;
}

/**
 * A `FLY TO HERE` target: pointer offset from the aircraft symbol → a position.
 *
 * The mirror of `setpointFromPointer`, and it shares that function's three
 * conventions exactly — `dyPx` grows DOWN, screen-up is bearing 0 plus the map
 * rotation, and the result is expressed in ground metres. What differs is what
 * the pixels MEAN: there they are a fraction of the speed ring, here they are
 * map distance, so this is the one place in the module that needs a scale
 * (`metresPerPixel`, from `calibrateSurface`) rather than a ring.
 *
 * Flat-earth for the same reason `projectSetpoint` is: the surface covers a few
 * tens of metres, where a tangent plane is accurate to well under a centimetre.
 * The uncertainty that matters at this scale is the POINTER's, and that is
 * `flyToHereGate`'s answer — never this function's.
 */
export function targetFromPointer(
  position: LatLng,
  offset: PointerOffsetPx,
  metresPerPixel: number,
  options: { mapBearingDeg?: number } = {},
): ManualTarget {
  const dx = finiteOr(offset.dxPx, 0);
  const dy = finiteOr(offset.dyPx, 0);
  const mpp = Math.max(0, finiteOr(metresPerPixel, 0));
  const distanceM = Math.hypot(dx, dy) * mpp;

  if (distanceM === 0) {
    return { lat: position.lat, lng: position.lng, distanceM: 0, bearingDeg: null };
  }
  const bearingDeg = wrap360(finiteOr(options.mapBearingDeg, 0) + toDeg(Math.atan2(dx, -dy)));
  const bearing = toRad(bearingDeg);
  const moved = offsetPosition(
    position,
    distanceM * Math.cos(bearing),
    distanceM * Math.sin(bearing),
  );
  return { ...moved, distanceM, bearingDeg };
}

/**
 * The exact inverse of `targetFromPointer`: where a fixed ground position sits
 * on the surface, in pixels from the aircraft symbol.
 *
 * A dropped target pin is a point on the GROUND, not a point on the screen. As
 * the aircraft drifts under it the pixel offset changes and the position does
 * not, so the pin is stored as a position and redrawn through this — otherwise
 * it would ride along with the aircraft, which is the one thing a target must
 * never do.
 */
export function pointerFromTarget(
  position: LatLng,
  target: LatLng,
  metresPerPixel: number,
  options: { mapBearingDeg?: number } = {},
): PointerOffsetPx {
  const mpp = positiveOr(metresPerPixel, 1);
  const northM = (target.lat - position.lat) * METRES_PER_DEG_LAT;
  const eastM = (target.lng - position.lng) * metresPerDegLng(position.lat);

  // Rotate the ground vector into SCREEN axes: screen-up is `mapBearingDeg`, and
  // screen y grows down, which is where the sign on `dyPx` comes from.
  const rotation = toRad(wrap360(finiteOr(options.mapBearingDeg, 0)));
  const screenNorthM = northM * Math.cos(rotation) + eastM * Math.sin(rotation);
  const screenEastM = -northM * Math.sin(rotation) + eastM * Math.cos(rotation);

  return { dxPx: screenEastM / mpp, dyPx: -screenNorthM / mpp };
}
